import { SpinnerButton } from "components/shared/SpinnerButton";
import graphql from "babel-plugin-relay/macro";
import { AnimalPublicTagSearchResult, IAnimalSearchProps } from "components/animal/AnimalTagSearch/AnimalPublicTagSearchResult";
import { ErrorMessage } from "components/shared/ErrorMessage";
import { Message } from "shared/components/Message";
import { lostPetMessages } from "domain/lostPet/lostPetMessages";
import { AnimalPublicTagSearchQuery } from "generatedQueries/AnimalPublicTagSearchQuery.graphql";
import React, { FC, useCallback, useEffect, useState } from "react";
import { Recaptcha } from "shared/components/newFormInputs/Recaptcha";
import { Col, Form, Row } from "reactstrap";
import { sharedMessages } from "shared/sharedMessages";
import { FormLabel } from "shared/components/formInputs/FormLabel";
import { TextBox } from "shared/components/newFormInputs/TextBox";
import { useIntl } from "react-intl";
import { useForm } from "react-hook-form";
import { required } from "shared/validations/validations";
import { RecaptchaVersion } from "generatedQueries/AnimalPublicTagSearchResultQuery.graphql";
import { getRecaptchaTokenV3, loadRecaptchaV3 } from "shared/recaptchaV3Functions";
import { useLazyLoadQuery } from "react-relay/hooks";

const emptySearchProps: IAnimalSearchProps = { tag: null, recaptchaResponse: null, recaptchaVersion: "V3" };

interface IProps {
    maxAnimalsToDisplay?: number;
}

interface IForm {
    tag: string | null;
    recaptcha: string;
}

export const AnimalPublicTagSearch: FC<IProps> = ({ maxAnimalsToDisplay }) => {
    const { configuration } = useLazyLoadQuery<AnimalPublicTagSearchQuery>(query, {});
    const { formatMessage } = useIntl();
    const { control, register, handleSubmit } = useForm<IForm>();

    const [searchProps, setSearchProps] = useState<IAnimalSearchProps>(emptySearchProps);
    const [isLoading, setIsLoading] = useState(false);
    const [srStatusMessage, setSrStatusMessage] = useState<string>("");
    const [recaptchaVersion, setRecaptchaVersion] = useState<RecaptchaVersion>("V3");

    const onResultRendering = useCallback(() =>  {
        setIsLoading(false);
    },[]);

    const onSetRecaptchaVersion = useCallback((newRecaptchaVersion: RecaptchaVersion) => {
        setIsLoading(false);
        setRecaptchaVersion(newRecaptchaVersion);
    }, []);

    const {
        licensingRecaptchaSiteKey,
        licensingRecaptchaSiteKeyV3,
    } = configuration ?? {};

    useEffect(() => {
        if (licensingRecaptchaSiteKeyV3) {
            loadRecaptchaV3(licensingRecaptchaSiteKeyV3);
        }
    }, [licensingRecaptchaSiteKeyV3, loadRecaptchaV3]);

    if (!configuration) {
        return <ErrorMessage message={sharedMessages.requestFailedBody} heading={sharedMessages.requestFailedTitle} />;
    }

    if (recaptchaVersion === "V2" && !licensingRecaptchaSiteKey) {
        return <ErrorMessage message={sharedMessages.requestFailedBody} heading={sharedMessages.requestFailedTitle} />;
    }
    else if (recaptchaVersion === "V3" && !licensingRecaptchaSiteKeyV3) {
        return <ErrorMessage message={sharedMessages.requestFailedBody} heading={sharedMessages.requestFailedTitle}  />;
    }

    return (
        <main>
            <Row>
                <Col lg={{ size: 8, offset: 2 }}>
                    <h1 className="h2" id="lostPetSearch-title">
                        <Message message={lostPetMessages.lostPetSearchPageTitle} />
                    </h1>
                    <p className="text-muted">
                        <Message message={lostPetMessages.lostPetSearchPageMessage} />
                    </p>
                    <Form
                        aria-labelledby="lostPetSearch-title"
                        onSubmit={handleSubmit(onFormSubmit)}>
                        <FormLabel for="tag" message={lostPetMessages.tagNumberSearchLabel}>
                            <TextBox
                                id="tag"
                                name="tag"
                                placeholder={formatMessage(lostPetMessages.tagNumberSearchLabel)}
                                control={control}
                                register={register}
                                options={required<IForm, "tag">()} />
                        </FormLabel>
                        {recaptchaVersion === "V2" && <Recaptcha
                            name="recaptcha"
                            control={control}
                            sitekey={licensingRecaptchaSiteKey}
                            rules={required<IForm, "recaptcha">(lostPetMessages.recapchaValidationMessage)} />
                        }
                        <SpinnerButton
                            type="submit"
                            message={sharedMessages.searchButtonLabel}
                            isLoading={isLoading}
                            color="primary"
                            className="mr-3" />
                    </Form>
                    <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
                        {srStatusMessage}
                    </div>
                    <AnimalPublicTagSearchResult
                        {...searchProps}
                        onResultRendering={onResultRendering}
                        maxAnimalsToDisplay={maxAnimalsToDisplay}
                        onSetStatus={setSrStatusMessage}
                        onSetRecaptchaVersion={onSetRecaptchaVersion}
                    />
                </Col>
            </Row>
        </main>
    );

    async function onFormSubmit(values: IForm) {
        if (!values.tag || isLoading) {
            return;
        }
        setIsLoading(true);

        if (recaptchaVersion === "V3" && licensingRecaptchaSiteKeyV3) {
            try {
                values.recaptcha = await getRecaptchaTokenV3(licensingRecaptchaSiteKeyV3);
            }
            catch {
                onSetRecaptchaVersion("V2");
                return;
            }
        }

        if (!values.recaptcha) {
            setIsLoading(false);
            return;
        }

        setSearchProps({
            recaptchaResponse: values.recaptcha,
            tag: values.tag,
            recaptchaVersion,
        });
    }
};

const query = graphql`
    query AnimalPublicTagSearchQuery {
        configuration {
            licensingRecaptchaSiteKey
            licensingRecaptchaSiteKeyV3
        }
    }`;
